import { z } from 'zod';

// Auth Schemas
export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
});
export type LoginFormData = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});
export type RegisterFormData = z.infer<typeof registerSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  token: z.string().min(1, 'Reset token is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'New password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

// Lead Schema
export const leadSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  company: z.string().max(200).optional().or(z.literal('')),
  jobTitle: z.string().max(100).optional().or(z.literal('')),
  source: z.enum(['WEBSITE', 'REFERRAL', 'SOCIAL_MEDIA', 'COLD_CALL', 'EMAIL_CAMPAIGN', 'TRADE_SHOW', 'PARTNER', 'OTHER']).default('OTHER'),
  status: z.enum(['NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED', 'LOST']).default('NEW'),
  score: z.coerce.number().min(0).max(100).default(0),
  estimatedValue: z.coerce.number().min(0).optional(),
  description: z.string().optional().or(z.literal('')),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  postalCode: z.string().optional().or(z.literal('')),
  assignedToId: z.string().optional().or(z.literal('')),
});
export type LeadFormData = z.infer<typeof leadSchema>;

// Contact Schema
export const contactSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  mobile: z.string().max(20).optional().or(z.literal('')),
  jobTitle: z.string().max(100).optional().or(z.literal('')),
  department: z.string().max(100).optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  postalCode: z.string().optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
  linkedInUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
  companyId: z.string().optional().or(z.literal('')),
});
export type ContactFormData = z.infer<typeof contactSchema>;

// Company Schema
export const companySchema = z.object({
  name: z.string().min(1, 'Company name is required').max(200),
  industry: z.string().max(100).optional().or(z.literal('')),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  employeeCount: z.coerce.number().min(0).optional(),
  annualRevenue: z.coerce.number().min(0).optional(),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  postalCode: z.string().optional().or(z.literal('')),
});
export type CompanyFormData = z.infer<typeof companySchema>;

// Deal Schema
export const dealSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  value: z.coerce.number().min(0, 'Value must be positive'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  description: z.string().optional().or(z.literal('')),
  expectedCloseDate: z.string().optional().or(z.literal('')),
  pipelineId: z.string().min(1, 'Pipeline is required'),
  stageId: z.string().min(1, 'Stage is required'),
  companyId: z.string().optional().or(z.literal('')),
  assignedToId: z.string().optional().or(z.literal('')),
});
export type DealFormData = z.infer<typeof dealSchema>;

// Task Schema
export const taskSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().optional().or(z.literal('')),
  status: z.enum(['TODO', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']).default('TODO'),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']).default('MEDIUM'),
  dueDate: z.string().optional().or(z.literal('')),
  assignedToId: z.string().optional().or(z.literal('')),
  leadId: z.string().optional().or(z.literal('')),
  contactId: z.string().optional().or(z.literal('')),
  dealId: z.string().optional().or(z.literal('')),
});
export type TaskFormData = z.infer<typeof taskSchema>;

// Expense Schema
export const expenseSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().optional().or(z.literal('')),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  expenseDate: z.string().min(1, 'Expense date is required'),
  receiptNumber: z.string().optional().or(z.literal('')),
  merchantName: z.string().optional().or(z.literal('')),
  categoryId: z.string().min(1, 'Category is required'),
  accountId: z.string().optional().or(z.literal('')),
  isRecurring: z.boolean().default(false),
  recurringDay: z.coerce.number().min(1).max(31).optional(),
});
export type ExpenseFormData = z.infer<typeof expenseSchema>;

// ── Priced documents (invoice, quotation) ────────────────────────────────────
// ITEMIZED: the subtotal is derived from the items (qty × unit price).
// LUMP_SUM: the items are a description-only list and `subtotal` is typed in
// directly; tax and discount still apply on top of it.
const documentItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.coerce.number().min(0).default(0),
  unitPrice: z.coerce.number().min(0, 'Unit price must be positive').default(0),
  taxRate: z.coerce.number().min(0).max(100).default(0),
  // Description-only sub-points printed under the line; blanks are dropped on save.
  children: z.array(z.object({ description: z.string() })).default([]),
});

type PricedDocument = {
  pricingMode: 'ITEMIZED' | 'LUMP_SUM';
  subtotal: number;
  items: Array<{ quantity: number }>;
};

// Rules that depend on the mode, so they can't live on a single field.
function refinePricing(data: PricedDocument, ctx: z.RefinementCtx) {
  if (data.pricingMode === 'LUMP_SUM') {
    if (!(data.subtotal > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['subtotal'], message: 'Enter the total amount' });
    }
    return;
  }
  data.items.forEach((item, i) => {
    if (!(item.quantity > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['items', i, 'quantity'], message: 'Quantity must be greater than 0' });
    }
  });
}

// Invoice Schema
export const invoiceSchema = z.object({
  issueDate: z.string().min(1, 'Issue date is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  pricingMode: z.enum(['ITEMIZED', 'LUMP_SUM']).default('ITEMIZED'),
  subtotal: z.coerce.number().min(0).default(0),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  taxRate: z.coerce.number().min(0).max(100).default(0),
  discountAmount: z.coerce.number().min(0).default(0),
  notes: z.string().optional().or(z.literal('')),
  terms: z.string().optional().or(z.literal('')),
  billingCompanyName: z.string().optional().or(z.literal('')),
  billingName: z.string().optional().or(z.literal('')),
  billingEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  billingAddress: z.string().optional().or(z.literal('')),
  billingCity: z.string().optional().or(z.literal('')),
  billingState: z.string().optional().or(z.literal('')),
  billingCountry: z.string().optional().or(z.literal('')),
  billingPostalCode: z.string().optional().or(z.literal('')),
  billingTaxId: z.string().max(50).optional().or(z.literal('')),
  taxSupply: z.enum(['AUTO', 'INTRA_STATE', 'INTER_STATE']).default('AUTO'),
  dealId: z.string().optional().or(z.literal('')),
  contactId: z.string().optional().or(z.literal('')),
  signatureId: z.string().optional().or(z.literal('')),

  // Per-invoice overrides (default: pre-fill from org settings, editable)
  taxId: z.string().optional().or(z.literal('')),
  paymentBankName: z.string().optional().or(z.literal('')),
  paymentBankAccountName: z.string().optional().or(z.literal('')),
  paymentBankAccountNumber: z.string().optional().or(z.literal('')),
  paymentBankIfsc: z.string().optional().or(z.literal('')),
  paymentBankSwift: z.string().optional().or(z.literal('')),
  paymentUpiId: z.string().optional().or(z.literal('')),
  paymentPaypalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  paymentInstructions: z.string().optional().or(z.literal('')),

  items: z.array(documentItemSchema).min(1, 'At least one item is required'),
}).superRefine(refinePricing);
export type InvoiceFormData = z.infer<typeof invoiceSchema>;

// Quotation Schema
export const quotationSchema = z.object({
  issueDate: z.string().min(1, 'Issue date is required'),
  validUntil: z.string().min(1, 'Valid until is required'),
  pricingMode: z.enum(['ITEMIZED', 'LUMP_SUM']).default('ITEMIZED'),
  subtotal: z.coerce.number().min(0).default(0),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  taxRate: z.coerce.number().min(0).max(100).default(0),
  discountAmount: z.coerce.number().min(0).default(0),
  notes: z.string().optional().or(z.literal('')),
  terms: z.string().optional().or(z.literal('')),
  billingCompanyName: z.string().optional().or(z.literal('')),
  billingName: z.string().optional().or(z.literal('')),
  billingEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  billingAddress: z.string().optional().or(z.literal('')),
  billingCity: z.string().optional().or(z.literal('')),
  billingState: z.string().optional().or(z.literal('')),
  billingCountry: z.string().optional().or(z.literal('')),
  billingPostalCode: z.string().optional().or(z.literal('')),
  billingTaxId: z.string().max(50).optional().or(z.literal('')),
  taxSupply: z.enum(['AUTO', 'INTRA_STATE', 'INTER_STATE']).default('AUTO'),
  dealId: z.string().optional().or(z.literal('')),
  contactId: z.string().optional().or(z.literal('')),
  taxId: z.string().optional().or(z.literal('')),
  signatureId: z.string().optional().or(z.literal('')),
  items: z.array(documentItemSchema).min(1, 'At least one item is required'),
}).superRefine(refinePricing);
export type QuotationFormData = z.infer<typeof quotationSchema>;

// Organization Settings Schema
const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
export const organizationSettingsSchema = z.object({
  companyName: z.string().min(1, 'Company name is required').max(200),
  companyEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  companyPhone: z.string().max(50).optional().or(z.literal('')),
  companyWebsite: z.string().max(200).optional().or(z.literal('')),
  taxId: z.string().max(50).optional().or(z.literal('')),
  addressLine1: z.string().max(200).optional().or(z.literal('')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(100).optional().or(z.literal('')),
  country: z.string().max(100).optional().or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  primaryColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #010b17)'),
  accentColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #a855f7)'),
  highlightColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #22d3ee)'),
  bankName: z.string().max(200).optional().or(z.literal('')),
  bankAccountName: z.string().max(200).optional().or(z.literal('')),
  bankAccountNumber: z.string().max(50).optional().or(z.literal('')),
  bankIfsc: z.string().max(50).optional().or(z.literal('')),
  bankSwift: z.string().max(50).optional().or(z.literal('')),
  upiId: z.string().max(100).optional().or(z.literal('')),
  paypalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  paymentInstructions: z.string().optional().or(z.literal('')),
  companyPrefix: z.string().max(10).optional().or(z.literal('')),
  invoiceFooterText: z.string().max(500).optional().or(z.literal('')),
  invoicePrefix: z.string().max(20).default('INV'),
  nextInvoiceSequence: z.coerce.number().int().min(1, 'Must be at least 1').default(1),
  quotationFooterText: z.string().max(500).optional().or(z.literal('')),
  quotationPrefix: z.string().max(20).default('QUO'),
  nextQuotationSequence: z.coerce.number().int().min(1, 'Must be at least 1').default(1),
  quotationDefaultValidityDays: z.coerce.number().int().min(1, 'Must be at least 1').max(365).default(30),
  // Agreement defaults
  agreementPrefix: z.string().max(20).default('AGR'),
  nextAgreementSequence: z.coerce.number().int().min(1, 'Must be at least 1').default(1),
  lateFeePercent: z.coerce.number().min(0).max(100).default(2),
  clientInactivityDays: z.coerce.number().int().min(1).max(365).default(15),
  freeSupportDays: z.coerce.number().int().min(0).max(365).default(30),
  deliverableReviewDays: z.coerce.number().int().min(0).max(90).default(7),
  governingCountry: z.string().min(1, 'Required').max(100).default('India'),
  jurisdictionLocation: z.string().min(1, 'Required').max(200).default('Bangalore, Karnataka'),
  companyIdentifierLabel: z.string().min(1, 'Required').max(50).default('GSTIN'),
});
export type OrganizationSettingsFormData = z.infer<typeof organizationSettingsSchema>;

// ── Document Settings — per-tab subsets (each tab saves its own slice) ──

/** General tab: company identity, address, theme, company-wide prefix, tax id. */
export const generalSettingsSchema = z.object({
  companyName: z.string().min(1, 'Company name is required').max(200),
  companyEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  companyPhone: z.string().max(50).optional().or(z.literal('')),
  companyWebsite: z.string().max(200).optional().or(z.literal('')),
  taxId: z.string().max(50).optional().or(z.literal('')),
  companyIdentifierLabel: z.string().min(1, 'Required').max(50).default('GSTIN'),
  addressLine1: z.string().max(200).optional().or(z.literal('')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(100).optional().or(z.literal('')),
  country: z.string().max(100).optional().or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  primaryColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #010b17)'),
  accentColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #a855f7)'),
  highlightColor: z.string().regex(HEX_COLOR, 'Must be a hex color (e.g. #22d3ee)'),
  companyPrefix: z.string().max(10).optional().or(z.literal('')),
  taxMode: z.enum(['SINGLE', 'SPLIT']).default('SINGLE'),
  taxLabel: z.string().min(1, 'Required').max(30).default('Tax'),
});
export type GeneralSettingsFormData = z.infer<typeof generalSettingsSchema>;

/** Invoice tab: invoice numbering marker + sequence, footer, payment defaults. */
export const invoiceSettingsSchema = z.object({
  invoicePrefix: z.string().max(20).default('INV'),
  nextInvoiceSequence: z.coerce.number().int().min(1, 'Must be at least 1').default(1),
  invoiceFooterText: z.string().max(500).optional().or(z.literal('')),
  bankName: z.string().max(200).optional().or(z.literal('')),
  bankAccountName: z.string().max(200).optional().or(z.literal('')),
  bankAccountNumber: z.string().max(50).optional().or(z.literal('')),
  bankIfsc: z.string().max(50).optional().or(z.literal('')),
  bankSwift: z.string().max(50).optional().or(z.literal('')),
  upiId: z.string().max(100).optional().or(z.literal('')),
  paypalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  paymentInstructions: z.string().optional().or(z.literal('')),
});
export type InvoiceSettingsFormData = z.infer<typeof invoiceSettingsSchema>;

/** Quote tab: quotation numbering marker + sequence, footer, validity window. */
export const quoteSettingsSchema = z.object({
  quotationPrefix: z.string().max(20).default('QUO'),
  nextQuotationSequence: z.coerce.number().int().min(1, 'Must be at least 1').default(1),
  quotationFooterText: z.string().max(500).optional().or(z.literal('')),
  quotationDefaultValidityDays: z.coerce.number().int().min(1, 'Must be at least 1').max(365).default(30),
});
export type QuoteSettingsFormData = z.infer<typeof quoteSettingsSchema>;

/** Agreement tab: numbering marker + sequence + legal/policy defaults. */
export const agreementSettingsSchema = z.object({
  agreementPrefix: z.string().max(20).default('AGR'),
  nextAgreementSequence: z.coerce.number().int().min(1).default(1),
  lateFeePercent: z.coerce.number().min(0).max(100).default(2),
  clientInactivityDays: z.coerce.number().int().min(1).max(365).default(15),
  freeSupportDays: z.coerce.number().int().min(0).max(365).default(30),
  deliverableReviewDays: z.coerce.number().int().min(0).max(90).default(7),
  governingCountry: z.string().min(1, 'Required').max(100).default('India'),
  jurisdictionLocation: z.string().min(1, 'Required').max(200).default('Bangalore, Karnataka'),
});
export type AgreementSettingsFormData = z.infer<typeof agreementSettingsSchema>;

// Payment Schema
export const paymentSchema = z.object({
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  paymentMethod: z.enum(['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHECK', 'ONLINE', 'OTHER']),
  referenceNumber: z.string().optional().or(z.literal('')),
  paymentDate: z.string().min(1, 'Payment date is required'),
  notes: z.string().optional().or(z.literal('')),
  invoiceId: z.string().optional().or(z.literal('')),
  accountId: z.string().optional().or(z.literal('')),
});
export type PaymentFormData = z.infer<typeof paymentSchema>;

// Account Schema
export const accountSchema = z.object({
  name: z.string().min(1, 'Account name is required').max(200),
  type: z.enum(['BANK', 'CASH', 'CREDIT_CARD', 'ACCOUNTS_RECEIVABLE', 'ACCOUNTS_PAYABLE']),
  accountNumber: z.string().optional().or(z.literal('')),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  description: z.string().optional().or(z.literal('')),
  balance: z.coerce.number().default(0),
});
export type AccountFormData = z.infer<typeof accountSchema>;

// Transaction Schema
export const transactionSchema = z.object({
  type: z.enum(['INCOME', 'EXPENSE', 'TRANSFER']),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  description: z.string().min(1, 'Description is required'),
  referenceNumber: z.string().optional().or(z.literal('')),
  transactionDate: z.string().min(1, 'Transaction date is required'),
  accountId: z.string().min(1, 'Account is required'),
});
export type TransactionFormData = z.infer<typeof transactionSchema>;

// Budget Schema
export const budgetSchema = z.object({
  name: z.string().min(1, 'Budget name is required').max(200),
  amount: z.coerce.number().min(0.01, 'Amount must be greater than 0'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']).default('USD'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  description: z.string().optional().or(z.literal('')),
  categoryId: z.string().optional().or(z.literal('')),
});
export type BudgetFormData = z.infer<typeof budgetSchema>;

// Profile Schema
export const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  phone: z.string().max(20).optional().or(z.literal('')),
  avatarUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
});
export type ProfileFormData = z.infer<typeof profileSchema>;

// Note Schema
export const noteSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  leadId: z.string().optional(),
  contactId: z.string().optional(),
  companyId: z.string().optional(),
  dealId: z.string().optional(),
});
export type NoteFormData = z.infer<typeof noteSchema>;

// Activity Schema
export const activitySchema = z.object({
  type: z.enum(['CALL', 'EMAIL', 'MEETING', 'NOTE', 'TASK', 'DEAL_CREATED', 'DEAL_STAGE_CHANGED', 'DEAL_WON', 'DEAL_LOST', 'LEAD_CONVERTED']),
  subject: z.string().min(1, 'Subject is required'),
  description: z.string().optional().or(z.literal('')),
  leadId: z.string().optional(),
  contactId: z.string().optional(),
  companyId: z.string().optional(),
  dealId: z.string().optional(),
});
export type ActivityFormData = z.infer<typeof activitySchema>;

// Expense Category Schema
export const expenseCategorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().optional().or(z.literal('')),
  color: z.string().optional().or(z.literal('')),
  icon: z.string().optional().or(z.literal('')),
  budgetLimit: z.coerce.number().min(0).optional(),
  parentId: z.string().optional().or(z.literal('')),
});
export type ExpenseCategoryFormData = z.infer<typeof expenseCategorySchema>;

// Pipeline Schema
export const pipelineSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().optional().or(z.literal('')),
  isDefault: z.boolean().default(false),
});
export type PipelineFormData = z.infer<typeof pipelineSchema>;

// Stage Schema
export const stageSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  probability: z.coerce.number().min(0).max(100).default(0),
  color: z.string().optional().or(z.literal('')),
});
export type StageFormData = z.infer<typeof stageSchema>;

// ── Payroll ──────────────────────────────────────────────────────────────────
// Statutory ids are free-form: the labels are configurable per deployment
// (PAN / SSN / TFN …), so validating a specific national format here would be
// wrong for every org that renames them.
export const employeeSchema = z.object({
  employeeCode: z.string().max(50).optional().or(z.literal('')),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().max(50).optional().or(z.literal('')),
  designation: z.string().max(120).optional().or(z.literal('')),
  department: z.string().max(120).optional().or(z.literal('')),
  employmentType: z.enum(['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERN', 'CONSULTANT']).default('FULL_TIME'),
  status: z.enum(['ACTIVE', 'ON_LEAVE', 'RESIGNED', 'TERMINATED']).default('ACTIVE'),
  dateOfJoining: z.string().min(1, 'Date of joining is required'),
  dateOfExit: z.string().optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
  taxIdentifier: z.string().max(50).optional().or(z.literal('')),
  socialSecurityId: z.string().max(50).optional().or(z.literal('')),
  pensionId: z.string().max(50).optional().or(z.literal('')),
  bankName: z.string().max(200).optional().or(z.literal('')),
  bankAccountName: z.string().max(200).optional().or(z.literal('')),
  bankAccountNumber: z.string().max(50).optional().or(z.literal('')),
  bankIfsc: z.string().max(50).optional().or(z.literal('')),
  userId: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
}).refine(
  (d) => !d.dateOfExit || !d.dateOfJoining || d.dateOfExit >= d.dateOfJoining,
  { message: 'Date of exit cannot be before the date of joining', path: ['dateOfExit'] },
);
export type EmployeeFormData = z.infer<typeof employeeSchema>;

export const payComponentSchema = z.object({
  code: z.string().min(1, 'Code is required').max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use UPPER_SNAKE_CASE, starting with a letter'),
  label: z.string().min(1, 'Label is required').max(100),
  type: z.enum(['EARNING', 'DEDUCTION', 'EMPLOYER_CONTRIBUTION']),
  calc: z.enum(['FIXED', 'PERCENT_OF_CTC', 'PERCENT_OF_BASIC', 'PERCENT_OF_GROSS', 'BALANCE']),
  defaultValue: z.coerce.number().min(0).optional(),
  isBasic: z.boolean().default(false),
  isTaxable: z.boolean().default(true),
  isStatutory: z.boolean().default(false),
  proratable: z.boolean().default(true),
  description: z.string().max(500).optional().or(z.literal('')),
  position: z.coerce.number().min(0).default(0),
  isActive: z.boolean().default(true),
})
  // BALANCE derives its amount, so it must not carry one; every other mode needs one.
  .refine((d) => d.calc === 'BALANCE' || d.defaultValue !== undefined, {
    message: 'A value is required for this calculation type', path: ['defaultValue'],
  })
  .refine((d) => !(d.calc === 'PERCENT_OF_GROSS' && d.type === 'EARNING'), {
    message: 'An earning cannot be a percentage of the gross it is part of', path: ['calc'],
  })
  .refine((d) => !(d.calc === 'BALANCE' && d.type !== 'EARNING'), {
    message: 'A balance component must be an earning', path: ['calc'],
  })
  .refine((d) => !(d.isBasic && d.type !== 'EARNING'), {
    message: 'Only an earning can be the basic component', path: ['isBasic'],
  })
  .refine((d) => !(d.isBasic && d.calc !== 'FIXED' && d.calc !== 'PERCENT_OF_CTC'), {
    message: 'The basic must use a fixed amount or % of CTC', path: ['isBasic'],
  });
export type PayComponentFormData = z.infer<typeof payComponentSchema>;

export const payrollSettingsSchema = z.object({
  taxIdentifierLabel: z.string().min(1, 'Required').max(50),
  socialSecurityIdLabel: z.string().min(1, 'Required').max(50),
  pensionIdLabel: z.string().min(1, 'Required').max(50),
  employeeCodePrefix: z.string().min(1, 'Required').max(10),
  nextEmployeeSequence: z.coerce.number().int().min(1),
  payslipPrefix: z.string().min(1, 'Required').max(20),
  nextPayslipSequence: z.coerce.number().int().min(1),
  payslipFooterText: z.string().max(500).optional().or(z.literal('')),
});
export type PayrollSettingsFormData = z.infer<typeof payrollSettingsSchema>;
